import { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import {
  Upload,
  Download,
  CheckCircle,
  AlertTriangle,
  Search,
  Users,
  ExternalLink,
  Pencil,
  UserX,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { paths } from "@shared-core/routing/routes";
import type {
  ImportRowRequest,
  ImportResponse,
} from "@shared-core/data/api-types";
import type { MarketingPerson } from "@shared-core/data/marketing-types";
import { EditPersonDialog } from "../app/components/domain/marketing/EditPersonDialog";
import { MarkInactiveDialog } from "../app/components/domain/marketing/MarkInactiveDialog";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";

// ─── CSV parsing ─────────────────────────────────────────────────────────────

interface ParseResult {
  rows: ImportRowRequest[];
  errors: string[];
}

export function parseConsentCsv(text: string): ParseResult {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length < 2) {
    return { rows: [], errors: ["File has no data rows."] };
  }

  // Detect delimiter: tab-separated (exported from Excel) or comma-separated.
  const delimiter = lines[0].includes("\t") ? "\t" : ",";
  const headers = lines[0].split(delimiter).map((h) => h.trim().toLowerCase());

  // company_name_hint matches the header the /contacts Tharstern-export CSV
  // already uses, so that export can be re-imported here without renaming
  // columns by hand. postal_address and tags are genuinely optional on the
  // backend (ImportRowRequest) — they were only ever required here as an
  // input-shape check, not a business rule, and requiring them rejected a
  // raw Tharstern export outright.
  const REQUIRED = [
    "first_name",
    "last_name",
    "email",
    "phone",
    "company_name_hint",
    "sales_rep_email",
    "email_consent",
  ] as const;
  const missing = REQUIRED.filter((h) => !headers.includes(h));
  if (missing.length > 0) {
    return {
      rows: [],
      errors: [
        `Missing columns: ${missing.join(", ")}. Use the template file.`,
      ],
    };
  }

  const idx = (col: string) => headers.indexOf(col);
  const get = (cols: string[], col: string) =>
    (idx(col) >= 0 ? (cols[idx(col)] ?? "") : "").trim();
  const rows: ImportRowRequest[] = [];
  const errors: string[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i], delimiter);
    const val = (col: string) => get(cols, col);

    // first_name/last_name are optional — Mailchimp doesn't require them
    // either (a nameless contact there just gets no personalised greeting).
    const firstName = val("first_name");
    const lastName = val("last_name");

    const tagsRaw = val("tags");
    const tags = tagsRaw
      ? tagsRaw
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
      : [];

    // Accepts both this page's own yes/no template and the boolean-style
    // TRUE/FALSE the /contacts Tharstern export writes for the same column.
    const consentRaw = val("email_consent").toLowerCase();
    const email_consent =
      consentRaw === "yes" || consentRaw === "true"
        ? true
        : consentRaw === "no" || consentRaw === "false"
          ? false
          : null;

    const contactTypeRaw = val("contact_type").toLowerCase();
    const lifecycle_stage =
      contactTypeRaw === "prospect" ||
      contactTypeRaw === "customer" ||
      contactTypeRaw === "suspect"
        ? contactTypeRaw
        : undefined;

    rows.push({
      first_name: firstName,
      last_name: lastName,
      email: val("email") || undefined,
      phone: val("phone") || undefined,
      company_name: val("company_name_hint") || undefined,
      postal_address: val("postal_address") || undefined,
      sales_rep_email: val("sales_rep_email") || undefined,
      account_code: val("account_code") || undefined,
      tags,
      email_consent: email_consent ?? undefined,
      lifecycle_stage,
    });
  }

  return { rows, errors };
}

function splitCsvLine(line: string, delimiter = ","): string[] {
  if (delimiter === "\t") {
    return line.split("\t");
  }
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === "," && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

// ─── Template download ────────────────────────────────────────────────────────

const TEMPLATE_FILENAME = "marketing_contacts_import_template.csv";

function buildTemplateCsv(): string {
  const header =
    "first_name,last_name,email,phone,company_name_hint,postal_address,sales_rep_email,tags,email_consent,contact_type,account_code";
  const example1 =
    "James,Hartley,j.hartley@example.com,07700 900123,Example Ltd,,sarah.jones@pollards.com,Healthcare,yes,prospect,EXL001";
  const example2 =
    "Priya,Mehta,priya.mehta@example.com,,Bright Print Ltd,,,Print,no,suspect,";
  return [header, example1, example2].join("\n");
}

// ─── Lifecycle badge ──────────────────────────────────────────────────────────

const LIFECYCLE_LABELS: Record<string, { label: string; className: string }> = {
  suspect: { label: "Suspect", className: "bg-zinc-100 text-zinc-600" },
  prospect: { label: "Prospect", className: "bg-blue-50 text-blue-700" },
  customer: { label: "Customer", className: "bg-green-50 text-green-700" },
};

function LifecycleBadge({ stage }: { stage: string }) {
  const config = LIFECYCLE_LABELS[stage] ?? {
    label: stage,
    className: "bg-zinc-100 text-zinc-600",
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
}

const CUSTOMER_STATUS_LABELS: Record<
  string,
  { label: string; className: string }
> = {
  never_ordered: {
    label: "Never ordered",
    className: "bg-zinc-100 text-zinc-600",
  },
  new: { label: "New", className: "bg-blue-50 text-blue-700" },
  existing: { label: "Existing", className: "bg-green-50 text-green-700" },
  lapsed: { label: "Lapsed", className: "bg-amber-50 text-amber-700" },
  reactivated: {
    label: "Reactivated",
    className: "bg-purple-50 text-purple-700",
  },
};

function CustomerStatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return <span className="text-muted-foreground/50 text-xs italic">—</span>;
  }
  const config = CUSTOMER_STATUS_LABELS[status] ?? {
    label: status,
    className: "bg-zinc-100 text-zinc-600",
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
}

const CONTACT_CATEGORY_LABELS: Record<
  string,
  { label: string; className: string }
> = {
  strategic: { label: "Strategic", className: "bg-purple-50 text-purple-700" },
  key: { label: "Key", className: "bg-blue-50 text-blue-700" },
  operational: { label: "Operational", className: "bg-zinc-100 text-zinc-600" },
  unknown: { label: "Unknown", className: "bg-zinc-100 text-zinc-500" },
};

function ContactCategoryBadge({ category }: { category: string | null }) {
  if (!category) {
    return <span className="text-muted-foreground/50 text-xs italic">—</span>;
  }
  const config = CONTACT_CATEGORY_LABELS[category] ?? {
    label: category,
    className: "bg-zinc-100 text-zinc-600",
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function InactiveBadge() {
  return (
    <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700">
      Inactive
    </span>
  );
}

type SegmentFilter = "all" | "strategic" | "key" | "client";

const SEGMENT_OPTIONS: ReadonlyArray<{ id: SegmentFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "strategic", label: "Strategic" },
  { id: "key", label: "Key" },
  { id: "client", label: "Client" },
];

type CustomerStatusFilter =
  "all" | "never_ordered" | "new" | "existing" | "lapsed" | "reactivated";

const CUSTOMER_STATUS_OPTIONS: ReadonlyArray<{
  id: CustomerStatusFilter;
  label: string;
}> = [
  { id: "all", label: "All" },
  { id: "never_ordered", label: "Never ordered" },
  { id: "new", label: "New" },
  { id: "existing", label: "Existing" },
  { id: "lapsed", label: "Lapsed" },
  { id: "reactivated", label: "Reactivated" },
];

type ContactCategoryFilter =
  "all" | "strategic" | "key" | "operational" | "unknown";

const CONTACT_CATEGORY_OPTIONS: ReadonlyArray<{
  id: ContactCategoryFilter;
  label: string;
}> = [
  { id: "all", label: "All" },
  { id: "strategic", label: "Strategic" },
  { id: "key", label: "Key" },
  { id: "operational", label: "Operational" },
  { id: "unknown", label: "Unknown" },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

type ImportState =
  | { kind: "idle" }
  | { kind: "parsed"; rows: ImportRowRequest[]; parseErrors: string[] }
  | { kind: "importing" }
  | {
      kind: "duplicates_found";
      rows: ImportRowRequest[];
      parseErrors: string[];
      result: ImportResponse;
    }
  | { kind: "done"; result: ImportResponse; parseErrors: string[] }
  | { kind: "error"; message: string };

export function MarketingPeoplePage() {
  const dataAdapter = useDataAdapter();
  const { downloadTextFile } = usePlatformCapabilities();
  const fileRef = useRef<HTMLInputElement>(null);
  const [importState, setImportState] = useState<ImportState>({ kind: "idle" });
  const [showImport, setShowImport] = useState(false);

  const [people, setPeople] = useState<MarketingPerson[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [editingPersonId, setEditingPersonId] = useState<string | null>(null);
  const [markingInactivePerson, setMarkingInactivePerson] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [segmentFilter, setSegmentFilter] = useState<SegmentFilter>("all");
  const [customerStatusFilter, setCustomerStatusFilter] =
    useState<CustomerStatusFilter>("all");
  const [hasEmailOnly, setHasEmailOnly] = useState(false);
  const [hasPhoneOnly, setHasPhoneOnly] = useState(false);
  const [contactCategoryFilter, setContactCategoryFilter] =
    useState<ContactCategoryFilter>("all");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const loadPeople = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dataAdapter.listMarketingPeople({
        q: debouncedQ || undefined,
        pageSize: 50,
        hasEmail: hasEmailOnly || undefined,
        hasPhone: hasPhoneOnly || undefined,
        customerStatus:
          customerStatusFilter === "all" ? undefined : customerStatusFilter,
        segmentId: segmentFilter === "all" ? undefined : segmentFilter,
        contactCategory:
          contactCategoryFilter === "all" ? undefined : contactCategoryFilter,
        includeInactive: includeInactive || undefined,
      });
      setPeople(res.items);
      setTotal(res.total);
    } catch (e) {
      console.error("marketing.contacts.list.failed", e);
    } finally {
      setLoading(false);
    }
  }, [
    dataAdapter,
    debouncedQ,
    hasEmailOnly,
    hasPhoneOnly,
    customerStatusFilter,
    segmentFilter,
    contactCategoryFilter,
    includeInactive,
  ]);

  useEffect(() => {
    void loadPeople();
  }, [loadPeople]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const { rows, errors } = parseConsentCsv(text);
      setImportState({ kind: "parsed", rows, parseErrors: errors });
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  async function handleImport(onDuplicate: "skip" | "replace" = "skip") {
    const source =
      importState.kind === "parsed"
        ? importState
        : importState.kind === "duplicates_found"
          ? { rows: importState.rows, parseErrors: importState.parseErrors }
          : null;
    if (!source || source.rows.length === 0) return;
    const { rows, parseErrors } = source;
    setImportState({ kind: "importing" });
    try {
      const result = await dataAdapter.importMarketingContacts(
        rows,
        onDuplicate,
      );
      if (onDuplicate === "skip" && result.duplicate_count > 0) {
        setImportState({ kind: "duplicates_found", rows, parseErrors, result });
      } else {
        setImportState({ kind: "done", result, parseErrors });
        void loadPeople();
      }
    } catch (e) {
      console.error("marketing.people.import.failed", e);
      setImportState({
        kind: "error",
        message: "Import failed. Please try again.",
      });
    }
  }

  function handleImportReset() {
    setImportState({ kind: "idle" });
    if (importState.kind === "done") setShowImport(false);
  }

  return (
    <div className="space-y-6">
      <MarketingNav active="contacts" />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl mb-1">Contacts</h1>
          <p className="text-sm text-muted-foreground">
            {loading
              ? "Loading…"
              : `${total} contact${total !== 1 ? "s" : ""} in the marketing engine`}
          </p>
          <Link
            to="/contacts"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-1"
          >
            <ExternalLink className="size-3" />
            Download Tharstern contacts
          </Link>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              downloadTextFile(
                TEMPLATE_FILENAME,
                buildTemplateCsv(),
                "text/csv",
              )
            }
          >
            <Download className="size-4" />
            Download template
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setShowImport(true);
              setImportState({ kind: "idle" });
            }}
          >
            <Upload className="size-4" />
            Import CSV
          </Button>
        </div>
      </div>

      {/* Import panel */}
      {showImport && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Import from CSV</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowImport(false)}
            >
              Close
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Use the template above. Column headers must match exactly.
            </p>

            {(importState.kind === "idle" || importState.kind === "parsed") && (
              <>
                <div
                  className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border p-10 cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const text = ev.target?.result as string;
                      const { rows, errors } = parseConsentCsv(text);
                      setImportState({
                        kind: "parsed",
                        rows,
                        parseErrors: errors,
                      });
                    };
                    reader.readAsText(file);
                  }}
                >
                  <Upload className="size-8 text-muted-foreground" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">
                      Click to choose a CSV, or drag it here
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      .csv files only
                    </p>
                  </div>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  aria-label="Contacts CSV file"
                  className="hidden"
                  onChange={handleFileChange}
                />

                {importState.kind === "parsed" && (
                  <div className="space-y-3">
                    {importState.parseErrors.length > 0 && (
                      <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm space-y-1">
                        <p className="font-medium text-amber-800">
                          {importState.parseErrors.length} row
                          {importState.parseErrors.length !== 1 ? "s" : ""}{" "}
                          skipped
                        </p>
                        {importState.parseErrors.map((e, i) => (
                          <p key={i} className="text-amber-700">
                            {e}
                          </p>
                        ))}
                      </div>
                    )}
                    {importState.rows.length === 0 ? (
                      <p className="text-sm text-red-600">
                        No valid rows to import. Fix the errors and re-upload.
                      </p>
                    ) : (
                      <div className="rounded-md bg-surface border border-border px-4 py-3 text-sm">
                        <p className="font-medium">
                          {importState.rows.length} contact
                          {importState.rows.length !== 1 ? "s" : ""} ready to
                          import
                        </p>
                        <p className="text-muted-foreground">
                          {importState.rows
                            .slice(0, 3)
                            .map((r) => `${r.first_name} ${r.last_name}`)
                            .join(", ")}
                          {importState.rows.length > 3
                            ? `, +${importState.rows.length - 3} more`
                            : ""}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleImport()}
                        disabled={importState.rows.length === 0}
                      >
                        Import {importState.rows.length} contact
                        {importState.rows.length !== 1 ? "s" : ""}
                      </Button>
                      <Button variant="outline" onClick={handleImportReset}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {importState.kind === "importing" && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Importing…
              </div>
            )}

            {importState.kind === "duplicates_found" && (
              <div className="space-y-4">
                <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-4 text-sm space-y-3">
                  <p className="font-medium text-amber-800 flex items-center gap-1">
                    <AlertTriangle className="size-4" />
                    {importState.result.duplicate_count} contact
                    {importState.result.duplicate_count !== 1 ? "s" : ""}{" "}
                    already exist
                  </p>
                  <p className="text-amber-700">
                    {importState.result.created} new contact
                    {importState.result.created !== 1 ? "s" : ""} were created.
                    What would you like to do with the{" "}
                    {importState.result.duplicate_count} duplicate
                    {importState.result.duplicate_count !== 1 ? "s" : ""}?
                  </p>
                  <p className="text-amber-700">
                    Replacing fills in the cells your file sets and adds its
                    tags to the ones each contact already has. Blank cells leave
                    the existing value as it is, and Tharstern details and
                    inactive status are kept.
                  </p>
                  {importState.result.duplicates.length > 0 && (
                    <ul className="text-amber-700 text-xs list-disc list-inside space-y-0.5">
                      {importState.result.duplicates.slice(0, 5).map((d, i) => (
                        <li key={i}>
                          {d.incoming_first_name} {d.incoming_last_name}
                          {d.incoming_email ? ` (${d.incoming_email})` : ""}
                        </li>
                      ))}
                      {importState.result.duplicates.length > 5 && (
                        <li>
                          …and {importState.result.duplicates.length - 5} more
                        </li>
                      )}
                    </ul>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => handleImport("replace")}>
                    Replace existing contacts
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setImportState({
                        kind: "done",
                        result: importState.result,
                        parseErrors: importState.parseErrors,
                      });
                      void loadPeople();
                    }}
                  >
                    Keep existing contacts
                  </Button>
                </div>
              </div>
            )}

            {importState.kind === "done" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="size-5" />
                  <span className="font-medium">
                    Import complete —{" "}
                    {importState.result.created > 0 &&
                      `${importState.result.created} contact${importState.result.created !== 1 ? "s" : ""} created`}
                    {importState.result.created > 0 &&
                      (importState.result.replaced ?? 0) > 0 &&
                      ", "}
                    {(importState.result.replaced ?? 0) > 0 &&
                      `${importState.result.replaced} updated`}
                    {importState.result.created === 0 &&
                      (importState.result.replaced ?? 0) === 0 &&
                      "no changes"}
                  </span>
                </div>
                {importState.parseErrors.length > 0 && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm space-y-1">
                    <p className="font-medium text-amber-800 flex items-center gap-1">
                      <AlertTriangle className="size-4" /> Rows skipped during
                      parsing
                    </p>
                    {importState.parseErrors.map((e, i) => (
                      <p key={i} className="text-amber-700">
                        {e}
                      </p>
                    ))}
                  </div>
                )}
                {importState.result.warnings.length > 0 && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm space-y-1">
                    <p className="font-medium text-amber-800 flex items-center gap-1">
                      <AlertTriangle className="size-4" /> Import warnings
                    </p>
                    {importState.result.warnings.map((w, i) => (
                      <p key={i} className="text-amber-700">
                        {w}
                      </p>
                    ))}
                  </div>
                )}
                {importState.result.account_code_checks.length > 0 && (
                  <div className="rounded-md border border-border px-4 py-3 text-sm space-y-2">
                    <p className="font-medium">
                      Account codes — check each is the right company
                    </p>
                    <p className="text-xs text-muted-foreground">
                      A contact's account code links its orders to campaign
                      results. Tharstern supplier and customer codes overlap, so
                      a supplier's code can name a different customer.
                    </p>
                    <table className="w-full text-left">
                      <thead className="text-xs text-muted-foreground">
                        <tr>
                          <th className="py-1 pr-3 font-normal">Contact</th>
                          <th className="py-1 pr-3 font-normal">Code</th>
                          <th className="py-1 font-normal">
                            Tharstern customer
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {importState.result.account_code_checks.map((c) => (
                          <tr key={c.row_index} className="border-t">
                            <td className="py-1 pr-3 break-all">
                              {c.email ?? `Row ${c.row_index}`}
                            </td>
                            <td className="py-1 pr-3">{c.account_code}</td>
                            <td className="py-1">
                              {c.tharstern_customer_name ?? (
                                <span className="text-amber-700">
                                  Not a customer code — not saved
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={handleImportReset}>
                  Import another file
                </Button>
              </div>
            )}

            {importState.kind === "error" && (
              <div className="text-sm p-6 text-center border border-border rounded-lg text-red-600">
                {importState.message}
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleImportReset}
                  >
                    Try again
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by name or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-md border border-border bg-background pl-9 pr-4 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      {/* Segment filter pills */}
      <div className="flex gap-2 flex-wrap">
        {SEGMENT_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setSegmentFilter(opt.id)}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${
              segmentFilter === opt.id
                ? "bg-primary text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Customer status filter pills */}
      <div className="flex gap-2 flex-wrap">
        {CUSTOMER_STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setCustomerStatusFilter(opt.id)}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${
              customerStatusFilter === opt.id
                ? "bg-primary text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Contact category filter pills */}
      <div className="flex gap-2 flex-wrap">
        {CONTACT_CATEGORY_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setContactCategoryFilter(opt.id)}
            className={`px-3 py-1 rounded-full text-xs transition-colors ${
              contactCategoryFilter === opt.id
                ? "bg-primary text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Contact-method filter pills */}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setHasEmailOnly((v) => !v)}
          className={`px-3 py-1 rounded-full text-xs transition-colors ${
            hasEmailOnly
              ? "bg-primary text-white"
              : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >
          With email
        </button>
        <button
          type="button"
          onClick={() => setHasPhoneOnly((v) => !v)}
          className={`px-3 py-1 rounded-full text-xs transition-colors ${
            hasPhoneOnly
              ? "bg-primary text-white"
              : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >
          With phone
        </button>
        <button
          type="button"
          onClick={() => setIncludeInactive((v) => !v)}
          className={`px-3 py-1 rounded-full text-xs transition-colors ${
            includeInactive
              ? "bg-primary text-white"
              : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >
          Show inactive
        </button>
      </div>

      {/* Contacts table */}
      {loading ? (
        <div className="text-center py-12 text-sm text-muted-foreground">
          Loading contacts…
        </div>
      ) : people.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <Users className="size-10 mx-auto text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            {debouncedQ
              ? "No contacts match your search."
              : "No contacts yet. Import a CSV to get started."}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface text-muted-foreground text-xs uppercase tracking-wide">
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Company</th>
                <th className="px-4 py-3 text-left font-medium">Email</th>
                <th className="px-4 py-3 text-left font-medium">Stage</th>
                <th className="px-4 py-3 text-left font-medium">
                  Customer status
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  Contact category
                </th>
                <th className="px-4 py-3 text-right font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {people.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-surface-hover transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-foreground">
                    {p.name ? (
                      <Link
                        to={paths.marketingPersonDetail({ personId: p.id })}
                        className="hover:underline"
                      >
                        {p.name}
                      </Link>
                    ) : (
                      <span className="italic text-muted-foreground/50">
                        (no name)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {p.company_name ?? (
                      <span className="italic text-muted-foreground/50">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {p.email ?? (
                      <span className="italic text-muted-foreground/50">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <LifecycleBadge stage={p.lifecycle} />
                      {p.isInactive && <InactiveBadge />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <CustomerStatusBadge status={p.customerStatus} />
                  </td>
                  <td className="px-4 py-3">
                    <ContactCategoryBadge category={p.contactCategory} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingPersonId(p.id)}
                        aria-label={`Edit ${p.name}`}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Pencil className="size-4" />
                      </button>
                      {!p.isInactive && (
                        <button
                          type="button"
                          onClick={() =>
                            setMarkingInactivePerson({ id: p.id, name: p.name })
                          }
                          aria-label={`Mark ${p.name} inactive`}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <UserX className="size-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > people.length && (
            <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
              Showing {people.length} of {total} contacts
            </div>
          )}
        </div>
      )}

      {editingPersonId && (
        <EditPersonDialog
          personId={editingPersonId}
          onClose={() => setEditingPersonId(null)}
          onSaved={() => void loadPeople()}
        />
      )}

      {markingInactivePerson && (
        <MarkInactiveDialog
          personId={markingInactivePerson.id}
          personName={markingInactivePerson.name}
          onClose={() => setMarkingInactivePerson(null)}
          onDone={() => void loadPeople()}
        />
      )}
    </div>
  );
}
